import React, { useState, useEffect } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Fieldset } from 'primereact/fieldset';
import { PriorityTag, StatusTag, StatCard } from './components/helper';
import KnowledgeBase from './KnowledgeBase';
import { useNavigate } from 'react-router-dom';
import { supportApi } from '../../services';
import { toast } from 'react-toastify';
import './styles/styles.scss';

import { hasPermission } from '../../components/protected/ProtectedSection';
import { HELP_SUPPORT_TICKET_TAB_VIEW, HELP_SUPPORT_KNOWLEDGE_TAB_VIEW, HELP_SUPPORT_FAQ_TAB_VIEW } from "../../config/permissions"

const HelpTab = () => (
    <div className="card">
        {/* <Fieldset legend="Important Business Function Links"> */}
            <ul>
                <p title="QAG (BE)"><a style={{ fontSize: '16px' }} href="mailto:qag@espire.com"><i className="pi pi-envelope mr-4"></i>qag@espire.com</a></p>
                <p title="HR"><a style={{ fontSize: '16px' }} href="mailto:hrd@espire.com"><i className="pi pi-envelope mr-4"></i>hrd@espire.com</a></p>
                <p title="Finance"><a style={{ fontSize: '16px' }} href="mailto:finance@espire.com"><i className="pi pi-envelope mr-4"></i>finance@espire.com</a></p>
                <p title="MIS"><a style={{ fontSize: '16px' }} href="mailto:mis@espire.com"><i className="pi pi-envelope mr-4"></i>mis@espire.com</a></p>
                <p title="GUR FAC"><a style={{ fontSize: '16px' }} href="mailto:gur.fac@espire.com"><i className="pi pi-envelope mr-4"></i>gur.fac@espire.com</a></p>
            </ul>
        {/* </Fieldset> */}
    </div>
);

const fmtDate = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy}, ${hh}:${min}:${ss}`;
};

const Support = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [categories, setCategories] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0, overdue: 0 });
    const [pageNum, setPageNum] = useState(0);
    const [pageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);

    const STATUS_OPTIONS = [
        { label: 'All Status', value: null },
        { label: 'Open', value: 'Open' },
        { label: 'In Progress', value: 'In Progress' },
        { label: 'Resolved', value: 'Resolved' },
        { label: 'Closed', value: 'Closed' },
    ];

    const loadTickets = async (page = 0) => {
        setLoading(true);
        try {
            const res = await supportApi.getMyTickets({
                pageNum: page, pageSize,
                search,
                status: statusFilter ?? '',
                category: categoryFilter ?? '',
            });
            const payload = res.data?.data;
            const all = Array.isArray(payload) ? payload : [];
            setTickets(all);
            setTotalRecords(res.data?.totalElement ?? all.length);
            setStats({
                total: res.data?.totalElement ?? all.length,
                open: all.filter(t => t.status === 'Open').length,
                inProgress: all.filter(t => t.status === 'In Progress').length,
                resolved: all.filter(t => t.status === 'Resolved').length,
                overdue: all.filter(t => t.slaBreach).length,
            });
        } catch {
            toast.error('Failed to load tickets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        supportApi.getCategories().then(res => {
            const list = res.data?.data ?? res.data ?? [];
            setCategories([
                { label: 'All Category', value: null },
                ...list.map(c => ({ label: c.name, value: c.id ?? c.name })),
            ]);
        }).catch(() => { });
    }, []);

    useEffect(() => { loadTickets(pageNum); }, [pageNum, statusFilter, categoryFilter]);

    const onSearch = () => { setPageNum(0); loadTickets(0); };

    return (
        <div className="support-page">
            <h1 className="ess-page-title">Help &amp; Support</h1>
            <p className="ess-page-sub">Raise, track and manage your support requests</p>

            <TabView>
                {hasPermission(HELP_SUPPORT_TICKET_TAB_VIEW) && (
                    <TabPanel header="My Tickets">
                        <div className="stat-grid stat-grid-5" style={{ marginBottom: 20 }}>
                            <StatCard num={stats.total} label="Total Raised" colorClass="sc-blue" />
                            <StatCard num={stats.open} label="Open" delta="Awaiting agent" colorClass="sc-orange" />
                            <StatCard num={stats.inProgress} label="In Progress" delta="Being worked on" colorClass="sc-purple" />
                            <StatCard num={stats.resolved} label="Resolved" colorClass="sc-green" />
                            <StatCard num={stats.overdue} label="Overdue" colorClass="sc-red" />
                        </div>

                        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                            <div className="search-wrap">
                                <i className="pi pi-search" />
                                <InputText
                                    placeholder="Search by ticket no or subject…"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && onSearch()}
                                    style={{ width: 280 }}
                                />
                            </div>
                            <Button label="Filter" icon="pi pi-filter" outlined severity="secondary" size="small" onClick={onSearch} />
                            <Dropdown
                                value={statusFilter}
                                options={STATUS_OPTIONS}
                                onChange={e => setStatusFilter(e.value)}
                                placeholder="All Status"
                                style={{ minWidth: 140 }}
                            />
                            <Dropdown
                                value={categoryFilter}
                                options={categories}
                                onChange={e => setCategoryFilter(e.value)}
                                placeholder="All Category"
                                style={{ minWidth: 160 }}
                            />
                            <Button
                                label="New Ticket"
                                icon="pi pi-plus"
                                style={{ marginLeft: 'auto', background: 'var(--blue)', border: 'none' }}
                                size="small"
                                onClick={() => navigate('/support/new-ticket')}
                            />
                        </div>

                        <DataTable value={Array.isArray(tickets) ? tickets : []} loading={loading} stripedRows rowClassName={r => {
                            if (r.slaBreach) return 'sla-breach';
                            if (r.isWatching) return 'watcher-row';
                            return '';
                        }}>
                            <Column field="ticketNo" header="Ticket ID" body={r => (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <strong
                                        style={{ color: r.slaBreach ? 'var(--red)' : 'var(--blue)', cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={() => navigate(`/support/ticket-details/${r.id}`)}
                                    >
                                        {r.ticketNo}
                                    </strong>
                                    {r.isWatching && (
                                        <span style={{ fontSize: 10, fontWeight: 700, background: '#f0f4ff', color: 'var(--blue)', border: '1px solid #bfdbfe', borderRadius: 10, padding: '1px 7px' }}>
                                            Watching
                                        </span>
                                    )}
                                </div>
                            )} style={{ width: 160 }} />
                            <Column field="subject" header="Subject" style={{ minWidth: 240 }} />
                            <Column field="category" header="Category" body={r => r.category?.name ?? r.category ?? ''} />
                            <Column field="priority" header="Priority" body={r => <PriorityTag value={r.priority} />} />
                            <Column field="status" header="Status" body={r => <StatusTag value={r.status} />} />
                            <Column field="createdAt" header="Raised On" body={r => fmtDate(r.createdAt)} />
                            <Column field="updatedAt" header="Last Updated" body={r => fmtDate(r.updatedAt)} />
                            <Column
                                field="assignedTo"
                                header="Assigned To"
                                body={r => {
                                    const dn = r.assignedAgent?.agentProfile?.displayName;
                                    return Array.isArray(dn) ? (dn[0] ?? '—') : (dn ?? '—');
                                }}
                            />
                        </DataTable>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, padding: '8px 4px' }}>
                            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Showing {tickets.length} of {totalRecords} entries</span>
                            <Paginator
                                first={pageNum * pageSize}
                                rows={pageSize}
                                totalRecords={totalRecords}
                                onPageChange={e => setPageNum(e.page)}
                                template="PrevPageLink PageLinks NextPageLink"
                            />
                        </div>
                    </TabPanel>
                )}

                {hasPermission(HELP_SUPPORT_KNOWLEDGE_TAB_VIEW) && (
                    <TabPanel header="Knowledge Base"><KnowledgeBase /></TabPanel>
                )}

                {hasPermission(HELP_SUPPORT_FAQ_TAB_VIEW) && (
                    <TabPanel header="FAQ">
                        <Accordion>
                            {['How do I raise a ticket?', 'What are the SLA timelines?', 'Can I reopen a resolved ticket?', 'How do I escalate my issue?'].map((q, i) => (
                                <AccordionTab key={i} header={q}>
                                    <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
                                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                                    </p>
                                </AccordionTab>
                            ))}
                        </Accordion>
                    </TabPanel>
                )}

                {/* ── Help Tab ── */}
                <TabPanel header="Business Function Links">
                    <HelpTab />
                </TabPanel>

            </TabView>
        </div>
    );
};

export default Support;